// src/modules/app-views/ticket-events/pages/Document/components/TicketEventsDocumentCardHeaderColumn3.jsx
// Card Header Column 3: current price (with trend icon) + total revenue + initial forecast
import { useEffect, useState } from 'react';
import SimpleColumn from 'components/SimpleComponents/SimpleColumn';
import { ArrowDownOutlined, ArrowUpOutlined, DollarOutlined, LineChartOutlined } from '@ant-design/icons';
import translateX from 'utils/translateX';
import financialService from 'services/financialService';
import axiosService from 'services/axiosService';
import { API, apiUrl } from 'constants/Api';

const TicketEventsDocumentCardHeaderColumn3 = ({ ...record }) => {
  const currentPrice = record?.currentPrice || 0;
  const minimumPrice = record?.minimumPrice || 0;
  const priceHistory = record?.priceHistory || [];
  const prevEntry = priceHistory.length >= 2 ? priceHistory[priceHistory.length - 2] : null;

  const priceDiff = prevEntry ? currentPrice - prevEntry.price : currentPrice - minimumPrice;
  const priceBase = prevEntry ? prevEntry.price : minimumPrice;
  const pricePct = priceBase > 0 ? ((priceDiff / priceBase) * 100).toFixed(1) : 0;
  const priceWentUp = priceDiff > 0;
  const priceWentDown = priceDiff < 0;

  const [totalRevenue, setTotalRevenue] = useState(0);

  useEffect(() => {
    const eventId = record?._id;
    if (!eventId || eventId === 'add') return;
    const fetchRevenue = async () => {
      try {
        const url = apiUrl.build({ url: API.TICKET_SALES.STATS, params: { eventId } });
        const [stats] = await axiosService.get({ url, returnAsObject: true });
        setTotalRevenue(stats?.sales?.totalRevenue || 0);
      } catch (_) {}
    };
    fetchRevenue();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record?._id]);

  // Initial forecast = minimumPrice * totalTickets
  const totalTickets = record?.totalTickets || 0;
  const initialForecast = minimumPrice * totalTickets;

  const trendIcon = priceWentUp ? (
    <ArrowUpOutlined style={{ color: 'inherit' }} />
  ) : priceWentDown ? (
    <ArrowDownOutlined style={{ color: 'inherit' }} />
  ) : null;

  const priceLabelSuffix = pricePct !== 0
    ? ` (${priceWentUp ? '+' : ''}${pricePct}%)`
    : '';

  const items = [
    {
      key: 'currentPrice',
      icon: trendIcon || <DollarOutlined />,
      label: `${financialService.formatCurrency(currentPrice)}${priceLabelSuffix}`,
      tooltipText: `${translateX('current_price')} (${pricePct > 0 ? '+' : ''}${pricePct}% ${translateX('vs_previous')})`,
      tooltipPlacement: 'right',
    },
    {
      key: 'totalRevenue',
      icon: <DollarOutlined />,
      label: totalRevenue > 0 ? financialService.formatCurrency(totalRevenue) : '-',
      tooltipText: translateX('total_revenue'),
      tooltipPlacement: 'right',
    },
    {
      key: 'initialForecast',
      icon: <LineChartOutlined />,
      label: initialForecast > 0 ? financialService.formatCurrency(initialForecast) : '-',
      tooltipText: translateX('initial_forecast'),
      tooltipPlacement: 'right',
    },
  ];

  return <SimpleColumn items={items} />;
};

export default TicketEventsDocumentCardHeaderColumn3;
